import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { goToMFCProfile, goToNewsFromModel } from '../../../actions/nav'
import { closeDialog } from '../../../app-state/modals/state.dialog'
import {
    openAuthenticationDrawer,
    openModelNotificationsDrawer,
    openSendMailDrawer,
} from '../../../app-state/modals/state.drawer'
import { enabledLounges } from '../../../mfcLogic/lounge'
import {
    isLevelPremium,
    isLevelPremiumOrHigher,
} from '../../../mfcLogic/userProperties/level'
import { isLounge } from '../../../mfcLogic/userProperties/user'
import i18n from '../../../utilities/i18n'
import { routerPush } from '../../../utilities/router'
import Button from '../../Buttons/Button'
import { AppStateType, useAppState } from '../../Hooks/useAppState'
import { useRouteSelector } from '../../Hooks/useRoute'
import { useUserSelector } from '../../Hooks/useUser'
import AddBookmark from '../../Icons/AddBookmark'
import AddFriend from '../../Icons/AddFriend'
import Bookmarked from '../../Icons/Bookmarked'
import Friended from '../../Icons/Friended'
import NoteOutline from '../../Icons/NoteOutline'
import NoteSolid from '../../Icons/NoteSolid'
import Tip from '../../Icons/Tip'
import Whiteboard from '../../Icons/Whiteboard'
import FlexGrid from '../../common/FlexGrid'
import { getThemeSpacing } from '../../styles/appTheme'
import APP_BREAKPOINTS, { maxWindowWidth } from '../../styles/breakpoints'
import { palette } from '../../styles/colors'
import EmbeddedNotes from '../MyNotes/EmbeddedNotes'

const topGridStyles = {
    marginBottom: getThemeSpacing(1),
    gap: getThemeSpacing(1),
}

const splitGridContentStyles = css({
    flex: '0 0 calc(50% - 4px)', //4px is half of the gap
})
const splitGridOverrides = css({
    gap: getThemeSpacing(1),
    marginBottom: getThemeSpacing(1),
})

const skinnyButtonStyles = css({
    width: 'auto',
})
const actionButtonStyles = css({
    fontWeight: 600,
    padding: getThemeSpacing(1),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    [maxWindowWidth(APP_BREAKPOINTS.sm)]: {
        fontSize: 12.5,
        lineHeight: 1.96,
    },
})
const ButtonGroupContainer = styled('div')({
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    justifyContent: 'space-around',
})

const selectedButtonStyles = css({
    border: `1px solid ${palette.greenMFC}`,
    color: palette.greenMFC,
    // fontWeight: 600,
    '& svg': {
        color: palette.greenMFC,
    },
})
const warningButtonStyles = css({
    border: `1px solid ${palette.mfcWarning}`,
    color: palette.mfcWarning,
    // fontWeight: 600,
    '& svg': {
        color: palette.mfcWarning,
    },
})
const iconStyles = css({
    marginRight: 4,
})
const buttonStyles = (selected: boolean, attention: boolean) => {
    if (attention) return warningButtonStyles
    if (selected) return selectedButtonStyles
    return null
}

const emptyFunction = () => {}

type ProfileActions = Array<ButtonAction | [ButtonAction, ButtonAction]>

interface ButtonAction {
    label: string
    selected: boolean
    onClick(): void
    hide?: boolean
    Icon?: any
    attention?: boolean
}

interface ActionProps {
    onOpenWhiteboard?(): void
    onOpenQuickReply?(): void
    onOpenProfile?(): void
    onOpenMessage?(): void
    onOpenRoom?(slug: string, options?: object): void
    onBookmark?(): void
    onOpenTip?(): void
    onFriend?(): void
    onIgnore?(): void
    onHide?(): void
    onPin?(): void
    onReportMenu?(): void
    showQuickReply?: boolean
    authenticated?: boolean
    canPin?: boolean
    reportMenu?: boolean
    mfcProfile?: boolean
    message?: boolean
    profile?: string
    header?: boolean
    isFriend: boolean
    isIgnored: boolean
    hasNote: boolean
    isHidden: boolean
    isBookmark: boolean
    hasWhiteboard: boolean
    isPublicShow: boolean
    isOffline: boolean
    isModel: boolean
    isRoomPinned?: boolean
    slug: string
    name: string
    id: number
}

const Actions = memo((props: ActionProps) => {
    const {
        onOpenWhiteboard = emptyFunction,
        onOpenQuickReply = emptyFunction,
        onOpenProfile = emptyFunction,
        onOpenMessage = emptyFunction,
        onReportMenu = emptyFunction,
        onOpenRoom = emptyFunction,
        onBookmark = emptyFunction,
        onOpenTip = emptyFunction,
        onFriend = emptyFunction,
        onIgnore = emptyFunction,
        onHide = emptyFunction,
        onPin = emptyFunction,
        showQuickReply,
        authenticated,
        header = true,
        canPin,
        reportMenu,
        mfcProfile,
        profile,
        message,
        isFriend,
        isIgnored,
        hasNote,
        isHidden,
        isBookmark,
        hasWhiteboard,
        isPublicShow,
        isRoomPinned,
        isOffline,
        isModel,
        slug,
        name,
        id,
    } = props

    const { slug: querySlug } = useRouteSelector('visibleQuery')
    const routeUser = useRouteSelector('slug')
    // const splitRoute = visibleRoute.split('/')
    // const regularRouterUser =
    //     splitRoute.length > 2 ? splitRoute[2] : splitRoute[1]
    const routeSlug = querySlug || routeUser //router.query.slug || regularRouterUser

    const sameUser = routeSlug === slug
    const inLounge = isLounge(routeSlug)
    const showingLounge = isLounge(slug)
    const { authenticated: isAuthenticated = authenticated } = useAppState(
        AppStateType.APP,
    )
    const rewardsPoints = useUserSelector('me', 'rewardsPoints')
    const level = useUserSelector('me', 'level')
    const notesRef = useRef<HTMLTextAreaElement>(undefined)
    const notesTimer = useRef<number>(0)
    const [showNotes, setShowNotes] = useState(false)
    const [expandLounges, setExpandLounges] = useState(false)
    const isPremiumMember = isLevelPremium(level)
    const isPremiumOrHigher = isLevelPremiumOrHigher(level)

    const handleOpenRoom = useCallback(() => {
        onOpenRoom(slug)
    }, [onOpenRoom, slug])

    const openModelInLounge = useCallback(
        (lounge: string) => () => {
            setExpandLounges(false)
            onOpenRoom(lounge, { loungeSurrogateID: id })
        },
        [onOpenRoom, id],
    )

    const handleViewInLounge = useCallback(() => {
        if (rewardsPoints < 1000) {
            openModelInLounge('lounge')()
        } else {
            setExpandLounges(true)
        }
    }, [rewardsPoints, openModelInLounge])

    const onNotes = useCallback(() => {
        if (!isAuthenticated) {
            closeDialog(true)
            openAuthenticationDrawer()
            return
        }
        setShowNotes(!showNotes)
    }, [isAuthenticated, showNotes])

    const onOpenMFCProfile = useCallback(() => {
        closeDialog()
        goToMFCProfile(slug)
    }, [slug])

    const onChatArchive = useCallback(() => {
        if (!isAuthenticated) {
            closeDialog(true)
            openAuthenticationDrawer()
            return
        }
        const archivesPath = `/chat-archives?id=${id}`
        routerPush(archivesPath)
    }, [isAuthenticated, id, name])

    const onSendMFCMail = useCallback(() => {
        closeDialog(true)
        openSendMailDrawer(name)
    }, [name])

    const onAddModelNotification = useCallback(() => {
        closeDialog(true)
        openModelNotificationsDrawer(name)
    }, [name])

    const onOpenNewsFeed = useCallback(() => {
        goToNewsFromModel(slug)
    }, [slug])

    const scrollNotesIntoView = useCallback(() => {
        cancelAnimationFrame(notesTimer.current)
        if (!notesRef.current) {
            notesTimer.current = requestAnimationFrame(scrollNotesIntoView)
            return
        }
        notesRef.current.scrollIntoViewIfNeeded?.()
    }, [notesRef])

    useEffect(() => {
        cancelAnimationFrame(notesTimer.current)
        setExpandLounges(false)
        setShowNotes(false)
    }, [slug])

    useEffect(() => {
        if (!showNotes) return
        scrollNotesIntoView()
    }, [showNotes])

    const profileActions: ProfileActions = !showingLounge
        ? [
              {
                  label: isOffline
                      ? i18n.t('menu.offlineTip')
                      : i18n.t('menu.tipModel'),
                  Icon: Tip,
                  selected: false,
                  hide: !isModel || !header || showingLounge,
                  onClick: onOpenTip,
              },
              showQuickReply
                  ? [
                        {
                            label: i18n.t('menu.sendMessage'),
                            selected: false,
                            hide: sameUser && !!message,
                            onClick: onOpenMessage,
                        },
                        {
                            label: i18n.t('menu.sendQuickReply'),
                            selected: false,
                            hide: sameUser && !!message,
                            onClick: onOpenQuickReply,
                        },
                    ]
                  : {
                        label: i18n.t('menu.sendMessage'),
                        selected: false,
                        hide: sameUser && !!message,
                        onClick: onOpenMessage,
                    },
              {
                  label: i18n.t('menu.whiteboard'),
                  Icon: Whiteboard,
                  selected: false,
                  hide: !hasWhiteboard,
                  onClick: onOpenWhiteboard,
              },
              {
                  label: i18n.t('menu.viewProfile'),
                  selected: false,
                  hide: !profile,
                  onClick: onOpenProfile,
              },
              {
                  label: i18n.t('menu.mfcProfile'),
                  hide: !mfcProfile,
                  selected: false,
                  onClick: onOpenMFCProfile,
              },
              {
                  label: !isBookmark
                      ? i18n.t('menu.bookmark')
                      : i18n.t('menu.bookmarked'),
                  Icon: !isBookmark ? AddBookmark : Bookmarked,
                  selected: !!isBookmark,
                  hide: !isModel,
                  onClick: onBookmark,
              },
              {
                  label: !isFriend
                      ? i18n.t('menu.addFriend')
                      : i18n.t('menu.friended'),
                  Icon: !isFriend ? AddFriend : Friended,
                  selected: !!isFriend,
                  onClick: onFriend,
              },
              {
                  label: !isIgnored
                      ? i18n.t(`menu.ignore${isModel ? 'Model' : 'User'}`)
                      : i18n.t(`menu.${isModel ? 'model' : 'user'}Ignored`),
                  selected: !!isIgnored,
                  attention: !!isIgnored,
                  onClick: onIgnore,
              },
              {
                  label: !isHidden
                      ? i18n.t('menu.hideModel')
                      : i18n.t('menu.modelHidden'),
                  selected: !!isHidden,
                  attention: !!isHidden,
                  hide: !isHidden
                      ? !isModel || isFriend || isBookmark
                      : !isModel,
                  onClick: onHide,
              },
              {
                  label: !showNotes
                      ? hasNote
                          ? i18n.t('menu.notes')
                          : i18n.t('menu.addNote')
                      : i18n.t('menu.closeNotes'),
                  Icon: !hasNote ? NoteOutline : NoteSolid,
                  selected: hasNote,
                  attention: showNotes,
                  onClick: onNotes,
              },
              {
                  label: 'Chat Archives',
                  selected: false,
                  hide: !isModel,
                  onClick: onChatArchive,
              },
              {
                  label: !isRoomPinned
                      ? i18n.t('menu.pinModel')
                      : i18n.t('menu.unpinModel'),
                  selected: !!isRoomPinned,
                  attention: !!isRoomPinned,
                  hide: !isModel || !canPin,
                  onClick: onPin,
              },
              {
                  label: i18n.t('menu.sendMFCMail'),
                  selected: false,
                  hide: !isPremiumOrHigher,
                  onClick: onSendMFCMail,
              },
              {
                  label: i18n.t('menu.addNotification'),
                  selected: false,
                  hide: !isPremiumOrHigher,
                  onClick: onAddModelNotification,
              },
              {
                  label: isModel
                      ? i18n.t('menu.reportModel')
                      : i18n.t('menu.reportUser'),
                  selected: false,
                  hide: !isAuthenticated || !reportMenu,
                  onClick: onReportMenu,
              },
              {
                  label: 'News Feed',
                  selected: false,
                  hide: !isModel,
                  onClick: onOpenNewsFeed,
              },
          ]
        : []

    return (
        <FlexGrid direction="column">
            <FlexGrid style={topGridStyles}>
                {header && (!isOffline || showingLounge) && (
                    <Button
                        data-testid="Button__EnterChat"
                        onClick={handleOpenRoom}
                        size="medium"
                        border
                        css={actionButtonStyles}
                        // css={profileButtonStyles}
                    >
                        {showingLounge
                            ? i18n.t('menu.enterLounge')
                            : i18n.t('menu.enterChat')}
                    </Button>
                )}
                {!inLounge && isAuthenticated && isPublicShow && (
                    <>
                        {!expandLounges && isPremiumMember ? (
                            <Button
                                data-testid="Button__ViewInLounge"
                                onClick={handleViewInLounge}
                                clean
                                border
                                size="medium"
                                css={actionButtonStyles}
                            >
                                {i18n.t('menu.viewInLounge')}
                            </Button>
                        ) : (
                            <ButtonGroupContainer>
                                {enabledLounges(rewardsPoints, level).map(
                                    (lounge) => {
                                        const Lounge = `Lounge${lounge.slice(
                                            6,
                                        )}`
                                        return (
                                            <Button
                                                key={lounge}
                                                data-testid={`Button__${Lounge}`}
                                                onClick={openModelInLounge(
                                                    lounge,
                                                )}
                                                clean
                                                border
                                                size="medium"
                                                css={[
                                                    actionButtonStyles,
                                                    skinnyButtonStyles,
                                                ]}
                                            >
                                                {i18n.t(`chat.${lounge}`)}
                                            </Button>
                                        )
                                    },
                                )}
                            </ButtonGroupContainer>
                        )}
                    </>
                )}
            </FlexGrid>
            <FlexGrid
                direction="row"
                wrap="wrap"
                overrides={splitGridOverrides}
            >
                {profileActions.map((action) => {
                    if (Array.isArray(action)) {
                        const [first, _second] = action
                        const {
                            label,
                            Icon,
                            selected,
                            attention,
                            // hide,
                            onClick = emptyFunction,
                        } = first
                        const sanitizedLabel = label.replace(/\s/g, '')
                        const testId = `Button__${sanitizedLabel}`
                        // <Grid  item xs={6}>
                        return (
                            <Button
                                key={sanitizedLabel}
                                data-testid={testId}
                                onClick={onClick}
                                css={[
                                    actionButtonStyles,
                                    buttonStyles(selected, attention),
                                    splitGridContentStyles,
                                ]}
                                size="medium"
                                border
                                clean
                            >
                                <>
                                    {!!Icon && <Icon css={iconStyles} />}
                                    {label}
                                </>
                            </Button>
                        )
                        // </Grid>
                    }
                    const {
                        label,
                        Icon,
                        selected,
                        attention,
                        hide,
                        onClick = emptyFunction,
                    } = action

                    const sanitizedLabel = label.replace(/\s/g, '')
                    const testId = `Button__${sanitizedLabel}`
                    if (hide) return null
                    return (
                        <Button
                            key={sanitizedLabel}
                            data-testid={testId}
                            onClick={onClick}
                            css={[
                                actionButtonStyles,
                                buttonStyles(selected, attention),
                                splitGridContentStyles,
                            ]}
                            size="medium"
                            border
                            clean
                        >
                            <>
                                {!!Icon && <Icon css={iconStyles} />}
                                {label}
                            </>
                        </Button>
                    )
                })}
            </FlexGrid>
            {showNotes && (
                <FlexGrid>
                    <EmbeddedNotes ref={notesRef} userId={id} />
                </FlexGrid>
            )}
        </FlexGrid>
    )
})

export default Actions
